import { useState } from "react";
import { Link } from 'react-router-dom';
import { Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

export default function Hero() {
  const [location, setLocation] = useState("");
  const [priceRange, setPriceRange] = useState([100000, 5000000]); // Naira: 100k - 5M
  const [propertyType, setPropertyType] = useState("");

  const buildSearchUrl = () => {
    const params = new URLSearchParams();
    if (location) params.append("location", location);
    if (priceRange) params.append("minPrice", priceRange[0].toString());
    if (priceRange) params.append("maxPrice", priceRange[1].toString());
    if (propertyType) params.append("type", propertyType);
    return `/properties?${params.toString()}`;
  };

  return (
    <section className="relative">
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-3xl p-8 md:p-12">
        <div className="max-w-3xl space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            Find Your Perfect Home
          </h1>
          <p className="text-lg text-muted-foreground">
            Discover thousands of properties for rent in your area. From cozy
            apartments to spacious houses.
          </p>

          <div className="bg-background rounded-xl p-4 shadow-lg space-y-4">
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Enter location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="border-none shadow-none focus-visible:ring-0"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <span>
                        ₦{(priceRange[0] / 1000000).toFixed(1)}M - ₦
                        {(priceRange[1] / 1000000).toFixed(1)}M
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-4">
                      <h4 className="font-medium">Price Range (₦ Naira)</h4>
                      <Slider
                        defaultValue={priceRange}
                        max={50000000}
                        min={0}
                        step={100000}
                        onValueChange={setPriceRange}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-sm">
                          ₦{(priceRange[0] / 1000000).toFixed(1)}M
                        </p>
                        <p className="text-sm">
                          ₦{(priceRange[1] / 1000000).toFixed(1)}M
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Property Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="condo">Condo</SelectItem>
                    <SelectItem value="townhouse">Townhouse</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Link to={buildSearchUrl()} className="w-full">
              <Button className="w-full" size="lg">
                <Search className="mr-2 h-4 w-4" /> Search Properties
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
